import { formatInTimeZone } from "date-fns-tz";
import { isEmpty } from "ramda";
import { ComponentProps, useMemo, useState } from "react";
import { IconButton, Typography, SidePanel, Loading, Icon, Skeleton, LinkButton, Card, Divider, Badge } from "@/components/atoms";
import {
  BookingMapState,
  BookingMarkerState,
  BookingTripMap,
  extractMarkers,
  extractPolylines,
  extractTripPoints,
  useBookingTripPaths,
} from "@/components/organisms";
import { getTripLogGuestViewUrl } from "@/helpers/bookingHelpers";
import { useProfile } from "@/hooks";
import { useGetBookingTripLogsQuery } from "@/redux/apis/booking/bookingApi";
import { BookingTripLog } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";

interface TripActivityProps {
  open: boolean;
  onClose: () => void;
}

export const TripActivity = ({ open, onClose }: TripActivityProps) => {
  const { id: bookingId, trackingHash, status, jobNumber } = useSelectedBooking();
  const { data: bookingTrip } = useGetBookingTripLogsQuery(bookingId, { skip: !open });
  const [markerState, setMarkerState] = useState<BookingMarkerState>({});

  const shareUrl = getTripLogGuestViewUrl(bookingId, trackingHash);
  const showShareUrl = status !== "complete_awaiting_review" && status !== "closed";

  return (
    <SidePanel open={open} onClose={onClose} width={800} focused>
      <section className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Icon name="Routing" size="lg" />
          <Typography variant="title">Trip Driver Activity ({jobNumber})</Typography>
        </div>
        <div className="ml-3 flex h-7 items-center">
          <IconButton iconName="ArrowRight" onClick={onClose} className="-mr-2" variant="custom" />
        </div>
      </section>
      <div className="flex h-full w-full flex-col overflow-y-auto p-5">
        <div className="flex-1 pb-24">
          <Badge type="info" isIconBadge message="View only mode. To make changes, go to the Close Job panel" className="mb-2 w-full" />
          {bookingTrip ? (
            <div className="flex flex-col">
              <TripBasicDetails />
              <TripMapWrapper
                readOnly
                bookingTrip={bookingTrip}
                markerState={markerState}
                setMarkerState={setMarkerState}
                showOverrideForm={false}
              />
            </div>
          ) : (
            <Loading />
          )}
          {bookingTrip ? (
            <TripBookingDetails readOnly bookingTrip={bookingTrip} markerState={markerState} setMarkerState={setMarkerState} />
          ) : (
            <Skeleton />
          )}
        </div>
        {showShareUrl && (
          <div className="border-gray fixed bottom-0 left-0 w-full border-t bg-white">
            <div className="flex items-center justify-end p-4">
              <LinkButton to={shareUrl} target="_blank" variant="secondary" size="lg" startIcon="Share">
                Share link to Client
              </LinkButton>
            </div>
          </div>
        )}
      </div>
    </SidePanel>
  );
};

export const TripBasicDetails = () => {
  const {
    dates: { pickup, timezone },
    jobNumber,
    passenger,
  } = useSelectedBooking();
  const { dateFormat } = useProfile();

  return (
    <ol className="flex gap-x-5 rounded-t-lg border border-neutral-mid-gray bg-neutral-surface-gray px-2.5 py-2">
      <li className="flex items-center gap-x-1.5">
        <Icon name="ArchiveBook" size="lg" />
        <Typography variant="paragraph">{jobNumber}</Typography>
      </li>
      <li className="flex items-center gap-x-1.5">
        <Icon name="Calendar" size="lg" />
        <Typography variant="paragraph">{formatInTimeZone(new Date(pickup), timezone, dateFormat)}</Typography>
      </li>
      <li className="flex items-center gap-x-1.5">
        <Icon name="User" size="lg" />
        <Typography variant="paragraph">{`Passenger: ${passenger.name}`}</Typography>
      </li>
    </ol>
  );
};
interface BookingTripProps extends ComponentProps<"div"> {
  bookingTrip: BookingTripLog;
  markerState: BookingMarkerState;
  setMarkerState: (markerState: BookingMarkerState) => void;
  readOnly?: boolean;
}
export const TripBookingDetails = ({ bookingTrip, markerState, setMarkerState, readOnly, className }: BookingTripProps) => {
  const {
    job: { timezone },
    bookingStops: { pickupLocationData, dropoffLocationData, waypointsLocationData },
    tripStops: { enRouteToPickupLocationData, passengerPickedUpLocationData, passengerDroppedOffLocationData },
    driverCurrentLocationData,
  } = bookingTrip;
  const hasTripDetails =
    enRouteToPickupLocationData ||
    passengerPickedUpLocationData ||
    passengerDroppedOffLocationData ||
    waypointsLocationData.length > 0 ||
    driverCurrentLocationData;

  const toggleMarker = (marker: keyof BookingMarkerState) => {
    if (readOnly) return;
    setMarkerState({ [marker]: !markerState[marker] });
  };

  return (
    <div className={clsx("mt-4 grid grid-cols-2 gap-x-3", className)}>
      {/* Left Column */}
      <div className="flex flex-col gap-y-3">
        <Card className="flex flex-col p-2.5">
          <div
            onClick={() => (pickupLocationData?.address ? toggleMarker("pickup") : null)}
            className={clsx("flex flex-col gap-y-1 rounded p-1.5", {
              "bg-primary-light text-primary-dark": markerState.pickup,
              "cursor-pointer hover:bg-neutral-gray": !readOnly,
              "cursor-default": readOnly,
            })}
          >
            <Typography variant="action">Pickup Location</Typography>
            <div className="flex flex-row gap-x-1.5">
              <Icon size="md" name="Location" className="flex-shrink-0" />
              <Typography variant="paragraph">{pickupLocationData?.address ?? "-"}</Typography>
            </div>
          </div>
        </Card>
        {hasTripDetails && (
          <Card className="flex flex-col p-2.5">
            <div
              onClick={() => (enRouteToPickupLocationData ? toggleMarker("en-route-to-pickup") : null)}
              className={clsx("flex flex-col gap-y-1 rounded p-1.5", {
                "bg-primary-light text-primary-dark": markerState["en-route-to-pickup"],
                "cursor-pointer hover:bg-neutral-gray": !readOnly,
                "cursor-default": readOnly,
              })}
            >
              <Typography variant="action">Driver On Route to Pickup</Typography>
              <div className="flex flex-row gap-x-1.5">
                <Icon size="md" name="UserSquare" className="flex-shrink-0" />
                <Typography variant="paragraph" className="truncate">
                  {enRouteToPickupLocationData
                    ? formatInTimeZone(new Date(enRouteToPickupLocationData.dateTime), timezone, "hh:mm:ss a")
                    : "-"}
                </Typography>
              </div>
            </div>
          </Card>
        )}
        {waypointsLocationData && !isEmpty(waypointsLocationData) && (
          <Card className="flex flex-col p-2.5">
            {waypointsLocationData.map((waypoint, index) => {
              const isLast = index === waypointsLocationData.length - 1;
              return (
                <>
                  <div
                    onClick={() => (passengerPickedUpLocationData ? toggleMarker(`${waypoint}-${index}`) : null)}
                    className={clsx("flex flex-col gap-y-1 rounded p-1.5", {
                      "bg-primary-light text-primary-dark": markerState[`${waypoint}-${index}`],
                      "cursor-pointer hover:bg-neutral-gray": !readOnly,
                      "cursor-default": readOnly,
                    })}
                  >
                    <div className="flex flex-row items-center gap-x-1.5">
                      <Icon size="md" name="LocationAdd" className="flex-shrink-0" />
                      <Typography variant="action" className="truncate">{`Scheduled Stop ${index + 1}`}</Typography>
                    </div>
                  </div>
                  {!isLast && <Divider className="my-3" />}
                </>
              );
            })}
          </Card>
        )}
        {/* {driver && driverCurrentLocationData && (
          <button
            onClick={() => toggleMarker("driver")}
            className={clsx("group inline-flex items-center gap-2.5 rounded-lg bg-neutral-surface-gray border border-neutral-mid-gray p-5", {
              "is-active bg-primary-light text-primary-dark": markerState["driver"],
              "cursor-auto": readOnly,
            })}
          >
            <Icon name="Car" variant="Bold" className="shrink-0 text-black" />
            <div className="flex flex-col truncate text-left">
              <Typography className="truncate leading-normal">{driver.name}</Typography>
              <Typography className="text-neutral-dark-gray group-[.is-active]:text-primary-dark">Driver Current Location</Typography>
            </div>
          </button>
        )} */}
      </div>

      {/* Right Column */}
      <div className="flex flex-col gap-y-3">
        <Card className="flex flex-col p-2.5">
          <div
            onClick={() => (dropoffLocationData?.address ? toggleMarker("dropoff") : null)}
            className={clsx("flex flex-col gap-y-1 rounded p-1.5", {
              "bg-primary-light text-primary-dark": markerState.dropoff,
              "cursor-pointer hover:bg-neutral-gray": !readOnly,
              "cursor-default": readOnly,
            })}
          >
            <Typography variant="action">Destination</Typography>
            <div className="flex flex-row gap-x-1.5">
              <Icon size="md" name="Location" className="flex-shrink-0" />
              <Typography variant="paragraph">{dropoffLocationData?.address ?? "-"}</Typography>
            </div>
          </div>
        </Card>
        {hasTripDetails && (
          <Card className="flex flex-col p-2.5">
            <div
              onClick={() => (passengerPickedUpLocationData ? toggleMarker("client-picked-up") : null)}
              className={clsx("flex flex-col gap-y-1 rounded p-1.5", {
                "bg-primary-light text-primary-dark": markerState["client-picked-up"],
                "cursor-pointer hover:bg-neutral-gray": !readOnly,
                "cursor-default": readOnly,
              })}
            >
              <Typography variant="action">Client Picked Up</Typography>
              <div className="flex flex-row gap-x-1.5">
                <Icon size="md" name="User" className="flex-shrink-0" />
                <Typography variant="paragraph" className="truncate">
                  {passengerPickedUpLocationData
                    ? formatInTimeZone(new Date(passengerPickedUpLocationData.dateTime), timezone, "hh:mm:ss a")
                    : "-"}
                </Typography>
              </div>
            </div>
            <Divider className="my-3" />
            <div
              onClick={() => (passengerDroppedOffLocationData ? toggleMarker("client-dropped-off") : null)}
              className={clsx("flex flex-col gap-y-1 rounded p-1.5", {
                "bg-primary-light text-primary-dark": markerState["client-dropped-off"],
                "cursor-pointer hover:bg-neutral-gray": !readOnly,
                "cursor-default": readOnly,
              })}
            >
              <Typography variant="action">Client Dropped Off</Typography>
              <div className="flex flex-row gap-x-1.5">
                <Icon size="md" name="User" className="flex-shrink-0" />
                <Typography variant="paragraph" className="truncate">
                  {passengerDroppedOffLocationData
                    ? formatInTimeZone(new Date(passengerDroppedOffLocationData.dateTime), timezone, "hh:mm:ss a")
                    : "-"}
                </Typography>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export const TripMapWrapper = ({
  bookingTrip,
  markerState,
  setMarkerState,
  readOnly = false,
  height = 645,
  showOverrideForm = true,
}: BookingTripProps & { height?: number; showOverrideForm?: boolean }) => {
  const { preTrip, driverOnWay, commenced } = useBookingTripPaths(bookingTrip);

  const mapState: BookingMapState = useMemo(() => {
    const points = !readOnly ? extractTripPoints(bookingTrip.locationData, bookingTrip.tripStops) : [];
    const polylines = extractPolylines(bookingTrip);
    const markers = extractMarkers(bookingTrip);

    return {
      tripPoints: points,
      markers,
      polylines: {
        onRoute: polylines.onRoute,
        onTrip: polylines.onTrip,
        preTrip,
        driverOnWay,
        commenced,
      },
    };
  }, [readOnly, bookingTrip, preTrip, driverOnWay, commenced]);

  return (
    <div
      className="overflow-hidden rounded-b-lg border border-t-0 border-neutral-mid-gray"
      style={{
        height: `${height}px`,
      }}
    >
      <BookingTripMap
        mode="admin"
        tripLog={bookingTrip}
        markerState={markerState}
        mapState={mapState}
        onToggle={setMarkerState}
        readOnly={readOnly}
        showOverrideForm={showOverrideForm}
      />
    </div>
  );
};
